import { PortalType } from './subscription.enums';

/**
 * User roles for Role-Based Access Control (RBAC)
 * System-wide roles assigned to users
 *
 * Note: For realm-specific roles, see RealmRole in realm.enums.ts
 */
export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  PLATFORM_ADMIN = 'platform_admin',
  BUSINESS_ADMIN = 'business_admin',
  BUSINESS_USER = 'business_user',
  BUSINESS_VIEWER = 'business_viewer',
  CONSUMER = 'consumer', // Consumer portal users (leads/prospects from Case Management)
  // Operational roles (Issue #1143 — Gap 22; #1557 added SENIOR_UNDERWRITER)
  LOAN_OFFICER = 'loan_officer',
  PROCESSOR = 'processor',
  CLOSER = 'closer',
  UNDERWRITER = 'underwriter',
  /** Senior UW — approval authority above standard UW (#1557). Approves sizer overrides, finalizes loans ≤ $1M without principal. */
  SENIOR_UNDERWRITER = 'senior_underwriter',
  ANALYST = 'analyst',
  OPS_MANAGER = 'ops_manager',
  PORTFOLIO_MANAGER = 'portfolio_manager',
  FUND_MANAGER = 'fund_manager',
  PRINCIPAL = 'principal',
  DRAW_INSPECTOR = 'draw_inspector',
  SERVICING = 'servicing',
}

/**
 * Roles allowed to access each portal type.
 * Used for defense-in-depth portal access validation.
 *
 * @see Issue #1099 — Admin portal missing role validation
 */
export const PORTAL_ALLOWED_ROLES: Record<PortalType, readonly UserRole[]> = {
  [PortalType.ADMIN]: [UserRole.SUPER_ADMIN, UserRole.PLATFORM_ADMIN],
  [PortalType.BUSINESS]: [
    UserRole.SUPER_ADMIN,
    UserRole.PLATFORM_ADMIN,
    UserRole.BUSINESS_ADMIN,
    UserRole.BUSINESS_USER,
    UserRole.BUSINESS_VIEWER,
    // Operational roles (Issue #1143) — all business-side personas
    UserRole.LOAN_OFFICER,
    UserRole.PROCESSOR,
    UserRole.CLOSER,
    UserRole.UNDERWRITER,
    UserRole.SENIOR_UNDERWRITER,
    UserRole.ANALYST,
    UserRole.OPS_MANAGER,
    UserRole.PORTFOLIO_MANAGER,
    UserRole.FUND_MANAGER,
    UserRole.PRINCIPAL,
    UserRole.DRAW_INSPECTOR,
    UserRole.SERVICING,
  ],
  [PortalType.CONSUMER]: [UserRole.CONSUMER],
} as const;

/**
 * User account status
 *
 * Tracks the lifecycle of a user account:
 * - INVITED: User has been invited but hasn't accepted yet
 * - ACTIVE: User account is active
 * - SUSPENDED: User account has been temporarily suspended
 * - DELETED: User account has been deleted (soft delete)
 */
export enum UserStatus {
  INVITED = 'invited',
  ACTIVE = 'active',
  SUSPENDED = 'suspended',
  DELETED = 'deleted',
}

/**
 * Team member roles for business portal invites
 *
 * Used when inviting users to a business team. Maps to
 * the more granular UserRole for actual permissions.
 */
export enum TeamMemberRole {
  ADMIN = 'admin',
  MANAGER = 'manager',
  MEMBER = 'member',
  VIEWER = 'viewer',
}

/**
 * Mapping from UserRole to TeamMemberRole for invite API
 *
 * Maps the detailed system roles to the simplified team member roles
 * used in the invite flow.
 */
export const USER_ROLE_TO_TEAM_ROLE: Record<string, TeamMemberRole> = {
  // Admin-level roles
  [UserRole.SUPER_ADMIN]: TeamMemberRole.ADMIN,
  [UserRole.PLATFORM_ADMIN]: TeamMemberRole.ADMIN,
  [UserRole.BUSINESS_ADMIN]: TeamMemberRole.ADMIN,
  // Manager-level roles (Issue #1143 — now using enum values)
  [UserRole.OPS_MANAGER]: TeamMemberRole.MANAGER,
  [UserRole.PORTFOLIO_MANAGER]: TeamMemberRole.MANAGER,
  [UserRole.FUND_MANAGER]: TeamMemberRole.MANAGER,
  [UserRole.PRINCIPAL]: TeamMemberRole.ADMIN,
  // Member-level roles (Issue #1143 — now using enum values)
  [UserRole.UNDERWRITER]: TeamMemberRole.MEMBER,
  [UserRole.SENIOR_UNDERWRITER]: TeamMemberRole.MANAGER,
  [UserRole.CLOSER]: TeamMemberRole.MEMBER,
  [UserRole.DRAW_INSPECTOR]: TeamMemberRole.MEMBER,
  [UserRole.SERVICING]: TeamMemberRole.MEMBER,
  [UserRole.ANALYST]: TeamMemberRole.MEMBER,
  [UserRole.LOAN_OFFICER]: TeamMemberRole.MEMBER,
  [UserRole.PROCESSOR]: TeamMemberRole.MEMBER,
  // Default roles
  [UserRole.BUSINESS_USER]: TeamMemberRole.MEMBER,
  [UserRole.BUSINESS_VIEWER]: TeamMemberRole.VIEWER,
};

/**
 * Agent status lifecycle
 */
export enum AgentStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  PAUSED = 'paused',
  ARCHIVED = 'archived',
}

/**
 * Subscription tier levels
 */
export enum SubscriptionTier {
  FREE = 'free',
  STARTER = 'starter',
  PROFESSIONAL = 'professional',
  ENTERPRISE = 'enterprise',
}

/**
 * Subscription status
 */
export enum SubscriptionStatus {
  ACTIVE = 'active',
  TRIALING = 'trialing',
  PAST_DUE = 'past_due',
  CANCELED = 'canceled',
  UNPAID = 'unpaid',
}

/**
 * Message types in conversations
 */
export enum MessageType {
  USER = 'user',
  ASSISTANT = 'assistant',
  SYSTEM = 'system',
  TOOL_CALL = 'tool_call',
  TOOL_RESULT = 'tool_result',
}

/**
 * Message status
 */
export enum MessageStatus {
  PENDING = 'pending',
  STREAMING = 'streaming',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

/**
 * Document processing status
 */
export enum DocumentStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

/**
 * Signup attempt status for signup approval workflow
 */
export enum SignupAttemptStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  AUTO_APPROVED = 'auto_approved',
  FAILED = 'failed',
}

/**
 * LLM providers supported by Model Gateway
 */
export enum LLMProvider {
  OPENAI = 'openai',
  ANTHROPIC = 'anthropic',
  GEMINI = 'gemini',
  GOOGLE_VERTEX = 'google_vertex',
  AZURE_OPENAI = 'azure_openai',
}

/**
 * API key status
 */
export enum ApiKeyStatus {
  ACTIVE = 'active',
  REVOKED = 'revoked',
  EXPIRED = 'expired',
}

/**
 * Webhook event types
 */
export enum WebhookEvent {
  MESSAGE_CREATED = 'message.created',
  MESSAGE_COMPLETED = 'message.completed',
  CONVERSATION_STARTED = 'conversation.started',
  CONVERSATION_ENDED = 'conversation.ended',
  DOCUMENT_PROCESSED = 'document.processed',
  AGENT_UPDATED = 'agent.updated',
}

/**
 * Integration types
 */
export enum IntegrationType {
  SLACK = 'slack',
  DISCORD = 'discord',
  TEAMS = 'teams',
  WEBHOOK = 'webhook',
  ZAPIER = 'zapier',
}

/**
 * Data source types for RAG
 */
export enum DataSourceType {
  DOCUMENT = 'document',
  WEB_PAGE = 'web_page',
  API = 'api',
  DATABASE = 'database',
}

/**
 * Search types for retrieval
 */
export enum SearchType {
  VECTOR = 'vector',
  KEYWORD = 'keyword',
  HYBRID = 'hybrid',
}
